import { useEffect, useRef, useState } from "react";
import Reveal from "@/components/Reveal";
import CollectCta from "@/components/the190/CollectCta";
import { trackViewContent, track } from "@/lib/analytics";

const SPEC = [
  "A3 archival giclée",
  "Edition of 100",
  "Signed",
  "Individually numbered",
  "Certificate of Authenticity",
];

/* ------------------- Single edition — editorial acquisition block ------------------- */

const EditionBlock = ({
  index,
  number,
  edition,
  price,
  primaryImage,
  primaryImageWebp,
  alt,
  reverse,
  anchorId,
}) => {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          trackViewContent(edition?.title || `Edition ${index + 1}`);
          io.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [edition, index]);

  const story = (edition?.story || "").split("\n").filter(Boolean);
  const previewParagraphs = story.slice(0, 1);
  const restParagraphs = story.slice(1);

  const ctaLabel = `Collect ${edition?.title} — ${price || "£199"}`;

  return (
    <section
      ref={ref}
      id={anchorId}
      className="bone-section py-24 md:py-40"
      data-testid={`the190-edition-${index + 1}`}
      style={{ scrollMarginTop: 80 }}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
        <Reveal
          className={`md:col-span-7 ${reverse ? "md:order-2" : ""}`}
          slow
          data-testid={`the190-edition-${index + 1}-image-wrap`}
        >
          <figure className="relative">
            <picture>
              {primaryImageWebp ? (
                <source srcSet={primaryImageWebp} type="image/webp" />
              ) : null}
              <img
                src={primaryImage}
                alt={alt}
                loading="lazy"
                className="w-full h-auto block"
                data-testid={`the190-edition-${index + 1}-image`}
              />
            </picture>
          </figure>
        </Reveal>

        <div className={`md:col-span-5 md:pr-4 ${reverse ? "md:order-1 md:pl-4 md:pr-0" : ""}`}>
          <Reveal delay={140}>
            <p className="hairline opacity-55 mb-6" data-testid={`the190-edition-${index + 1}-number`}>
              {number} / Chapter II
            </p>
            <h2
              className="font-wordmark text-5xl md:text-6xl leading-[1.02]"
              data-testid={`the190-edition-${index + 1}-title`}
            >
              {edition?.title}
            </h2>
            <p className="hairline opacity-55 mt-4">Nick Fraser · 1996</p>
            <p className="font-serif italic text-lg md:text-xl opacity-70 mt-3">
              Chapter II Collector Edition
            </p>

            <ul className="mt-10 border-t border-[color:var(--rule)] max-w-md">
              {SPEC.map((s) => (
                <li
                  key={s}
                  className="hairline py-3.5 border-b border-[color:var(--rule)] opacity-75"
                >
                  {s}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex items-baseline gap-8">
              <p
                className="font-serif italic text-5xl md:text-6xl"
                data-testid={`the190-edition-${index + 1}-price`}
              >
                {price || "£199"}
              </p>
            </div>

            <div className="mt-8">
              <CollectCta
                label={ctaLabel}
                content={`edition-${index + 1}`}
                href={edition?.link}
                testid={`the190-edition-${index + 1}-cta`}
              />
            </div>

            {story.length > 0 ? (
              <div className="mt-12 max-w-lg" data-testid={`the190-edition-${index + 1}-story`}>
                {previewParagraphs.map((p, i) => (
                  <p
                    key={`p-${i}`}
                    className="font-serif italic text-lg md:text-xl leading-[1.55] opacity-80"
                  >
                    {p}
                  </p>
                ))}
                {restParagraphs.length > 0 ? (
                  <>
                    {expanded ? (
                      <div className="mt-4 space-y-4">
                        {restParagraphs.map((p, i) => (
                          <p
                            key={`rp-${i}`}
                            className="font-serif italic text-lg md:text-xl leading-[1.55] opacity-80"
                          >
                            {p}
                          </p>
                        ))}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => {
                        setExpanded((v) => !v);
                        track("story_toggle", {
                          edition: edition?.title,
                          state: expanded ? "collapse" : "expand",
                        });
                      }}
                      className="hairline underline underline-offset-8 opacity-70 hover:opacity-100 transition-opacity duration-500 mt-8"
                      data-testid={`the190-edition-${index + 1}-story-toggle`}
                      aria-expanded={expanded}
                    >
                      {expanded ? "Close story −" : "Read the story +"}
                    </button>
                  </>
                ) : null}
              </div>
            ) : null}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ------------------------- LOOK CLOSER — cinematic break ------------------------- */

export const LookCloser = () => (
  <section
    id="look-closer"
    data-testid="the190-look-closer"
    className="relative w-full bg-black text-[color:var(--bone)]"
    style={{ scrollMarginTop: 80 }}
  >
    <div className="relative w-full" style={{ height: "min(90svh, 900px)" }}>
      <picture>
        <source srcSet="/collector/look-closer-gold.webp" type="image/webp" />
        <img
          src="/collector/look-closer-gold.jpg"
          alt="Extreme close-up of the gold line work in Universal Soul of Stars by Nick Fraser"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      </picture>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 30%, rgba(0,0,0,0.2) 70%, rgba(0,0,0,0.75) 100%)",
        }}
      />
      <div className="absolute inset-0 flex flex-col justify-between p-8 md:p-16">
        <Reveal slow>
          <p className="hairline opacity-70">Look Closer</p>
        </Reveal>
        <div className="max-w-lg self-end md:self-start">
          <Reveal delay={160}>
            <p className="font-serif italic text-2xl md:text-3xl leading-[1.35] opacity-95">
              Intricate line, colour and detail — preserved in a signed archival
              edition.
            </p>
            <button
              type="button"
              onClick={() => {
                track("cta_click", { cta: "look-closer-edition" });
                document
                  .getElementById("edition-universal-soul-of-stars")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className="hairline underline underline-offset-8 opacity-80 hover:opacity-100 transition-opacity duration-500 mt-8"
              data-testid="the190-look-closer-cta"
            >
              The edition ↓
            </button>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------- THE PHYSICAL EDITION -------------------------- */

const PHYSICAL_FACETS = [
  { label: "Archival Giclée", body: "A3 format" },
  { label: "Signed", body: "Individually signed" },
  { label: "Numbered", body: "Strict edition of 100" },
  { label: "Authenticated", body: "Certificate of Authenticity" },
];

export const PhysicalEdition = () => (
  <section
    id="the-edition"
    className="bone-section py-28 md:py-44"
    data-testid="the190-physical"
    style={{ scrollMarginTop: 80 }}
  >
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-start">
      <Reveal className="md:col-span-6" slow>
        <p className="hairline opacity-60 mb-6">The Physical Edition</p>
        <h2 className="font-wordmark text-5xl md:text-7xl leading-[1.02]">The Edition.</h2>
        <p className="font-serif italic text-xl md:text-2xl leading-[1.45] opacity-85 mt-10 max-w-md">
          Each Collector Edition is individually signed and numbered, and
          accompanied by its own Certificate of Authenticity from the Nick Fraser
          Archive.
        </p>
        <div className="mt-14 border-t border-[color:var(--rule)] max-w-md">
          {PHYSICAL_FACETS.map((f) => (
            <div
              key={f.label}
              className="py-5 border-b border-[color:var(--rule)] grid grid-cols-[1fr_auto] items-baseline gap-6"
              data-testid={`the190-physical-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <p className="hairline text-[color:var(--oxblood)]">{f.label}</p>
              <p className="font-serif italic text-lg opacity-80 text-right">{f.body}</p>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal className="md:col-span-6" delay={140}>
        <figure>
          <picture>
            <source srcSet="/collector/universal-soul-gallery.webp" type="image/webp" />
            <img
              src="/collector/universal-soul-gallery.jpg"
              alt="Universal Soul of Stars — signed archival giclée edition photographed at gallery angle, showing the frame, mount and signature"
              className="w-full h-auto block"
              loading="lazy"
              data-testid="the190-physical-image"
            />
          </picture>
          <figcaption className="hairline opacity-55 mt-4">
            Signature detail · Universal Soul of Stars
          </figcaption>
        </figure>
      </Reveal>
    </div>
  </section>
);

/* -------------------------- THE COLLECTOR PAIR -------------------------- */

export const CollectorPair = ({ data }) => (
  <section
    id="collector-pair"
    data-testid="the190-pair"
    className="text-[color:var(--bone)] py-28 md:py-44"
    style={{
      backgroundColor: "#141414",
      scrollMarginTop: 80,
    }}
  >
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
      <Reveal className="md:col-span-7" slow>
        <figure>
          <picture>
            <source srcSet="/collector/pair-on-floor.webp" type="image/webp" />
            <img
              src="/collector/pair-on-floor.jpg"
              alt="The Collector Pair — Tribal Goddess and Universal Soul of Stars framed together, the complete Chapter II edition pairing"
              className="w-full h-auto block"
              loading="lazy"
              data-testid="the190-pair-image"
            />
          </picture>
        </figure>
      </Reveal>

      <Reveal className="md:col-span-5" delay={140}>
        <p className="hairline opacity-60 mb-6">The Complete Chapter II Edition</p>
        <h2 className="font-wordmark text-5xl md:text-7xl leading-[1.02]">
          The Collector Pair.
        </h2>
        <p className="font-serif italic text-lg md:text-xl leading-[1.55] opacity-80 mt-10 max-w-md">
          Tribal Goddess and Universal Soul of Stars together — two signed and
          individually numbered editions, each accompanied by its own
          Certificate of Authenticity.
        </p>
        <p
          className="font-serif italic text-5xl md:text-6xl mt-12"
          data-testid="the190-pair-price"
        >
          {data?.price_pair || "£350"}
        </p>
        <p className="hairline opacity-60 mt-5">
          Individual editions {data?.price_single || "£199"} each &nbsp;·&nbsp; Collector Pair saves £48
        </p>
        <div className="mt-10">
          <CollectCta
            label={`Collect the Pair — ${data?.price_pair || "£350"}`}
            content="collector-pair"
            href={data?.pair_link}
            testid="the190-pair-cta"
          />
        </div>
      </Reveal>
    </div>
  </section>
);

/* -------------------------- EDITIONS WRAPPER -------------------------- */

export const Editions190 = ({ data }) => (
  <div data-testid="the190-editions" id="editions">
    <EditionBlock
      index={0}
      number="01"
      anchorId="edition-tribal-goddess"
      edition={data?.edition_one}
      price={data?.price_single}
      primaryImage="/collector/tribal-goddess-red-wall.jpg"
      primaryImageWebp="/collector/tribal-goddess-red-wall.webp"
      alt="Tribal Goddess by Nick Fraser — Chapter II Collector Edition, framed archival giclée print on a warm red gallery wall"
    />
    <LookCloser />
    <EditionBlock
      index={1}
      number="02"
      anchorId="edition-universal-soul-of-stars"
      reverse
      edition={data?.edition_two}
      price={data?.price_single}
      primaryImage="/collector/universal-soul-brick.jpg"
      primaryImageWebp="/collector/universal-soul-brick.webp"
      alt="Universal Soul of Stars by Nick Fraser — Chapter II Collector Edition, framed on an exposed brick wall in a collector's interior"
    />
    <PhysicalEdition />
    <CollectorPair data={data} />
  </div>
);

export default Editions190;
